import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { lastValueFrom } from 'rxjs';
import { I18nService } from '../../../../../@core/services/i18n/i18n.service';
import { UiLocaleService } from '../../../../../@core/services/i18n/ui-locale.service';
import { ModalService } from '../../../../../@core/services/modal/modal.service';
import { AddressTransportRoute, ProductVendorTransportRoute, TransportRoute, TransportRouteRole } from '../../../../../generated/generated-types';
import { TenantService } from '../../../../../shared/services/tenant.service';
import { AddressTransportRouteService } from '../../services/address-transport-route.service';
import { ProductVendorTransportRouteService } from '../../services/product-vendor-transport-route.service';
import { TransportRouteService } from '../../services/transport-route.service';
import { transportRouteIcon } from '../../utils/transport-route-icon.util';

/** A route of this product from this vendor: the vendor's values (if the vendor has the route) and the product's own. */
interface ProductRouteRow {
  key: string;
  transportRouteId: string | null;
  vendor: AddressTransportRoute | null;
  saved: ProductVendorTransportRoute | null;
  transitDays: number | null;
  fixedCost: number | null;
  costPerUnit: number | null;
  notes: string;
  /** Saved flags of the product: its own default, switched off for it. */
  isDefault: boolean;
  inactive: boolean;
}

type RouteValues = Pick<ProductRouteRow, 'transitDays' | 'fixedCost' | 'costPerUnit' | 'notes'>;
type RouteFlags = { isDefault?: boolean; inactive?: boolean };

/** Own values of a product entry – without them (and without flags) the entry has nothing to keep. */
function hasOwnValues(values: RouteValues | Partial<ProductVendorTransportRoute>): boolean {
  return values.transitDays != null || values.fixedCost != null || values.costPerUnit != null || !!values.notes;
}

/** What the server has for a row – a flag change writes these, not what is typed but unsaved. */
function savedValuesOf(row: ProductRouteRow): RouteValues {
  return {
    transitDays: row.saved?.transitDays ?? null,
    fixedCost: row.saved?.fixedCost ?? null,
    costPerUnit: row.saved?.costPerUnit ?? null,
    notes: row.saved?.notes ?? '',
  };
}

/**
 * Transport routes of one product from one vendor (sub-tab of the vendor relation in the product's purchasing
 * tab): the vendor's routes as defaults (grey), product values override them – an excavator travels differently
 * than a screw. Empty fields keep the vendor value; "back to vendor" removes the product values; a route can
 * also be kept for this product only. Every route can be switched off for the product or made its default
 * (the star; the product's default wins over the vendor's, none left on = the vendor delivers without route).
 */
@Component({
  selector: 'app-product-vendor-transport-routes-widget',
  templateUrl: './product-vendor-transport-routes-widget.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [FormsModule, TranslatePipe, ButtonModule, InputNumberModule, InputTextModule, MessageModule, SelectModule, TableModule, TooltipModule],
})
export class ProductVendorTransportRoutesWidgetComponent {
  private readonly service = inject(ProductVendorTransportRouteService);
  private readonly addressRoutes = inject(AddressTransportRouteService);
  private readonly catalogService = inject(TransportRouteService);
  private readonly i18n = inject(I18nService);
  private readonly uiLocale = inject(UiLocaleService);
  private readonly modal = inject(ModalService);
  private readonly tenantService = inject(TenantService);

  readonly vendorPriceListAddressLinkId = input.required<string>();
  readonly addressId = input.required<string>();
  /** Currency of the vendor's price list – costs are entered and shown in it, the tenant's otherwise. */
  readonly currency = input<string | null | undefined>(null);

  private readonly localeVersion = toSignal(this.uiLocale.uiStateChanges$, { initialValue: null });
  /** Numbers are typed in the language of the UI, not of the browser ("2,5" in German stays 2.5). */
  readonly locale = computed(() => {
    this.localeVersion();
    return this.uiLocale.intlLocale;
  });
  readonly currencyCode = computed(() => this.currency() || this.tenantService.currentTenantSig()?.currency || 'EUR');

  readonly catalog = signal<TransportRoute[]>([]);
  readonly rows = signal<ProductRouteRow[]>([]);
  readonly loading = signal<boolean>(false);
  readonly busyKey = signal<string | null>(null);
  readonly error = signal<string | null>(null);

  readonly catalogById = computed(() => new Map(this.catalog().map((route) => [route.id, route])));
  readonly unusedRoutes = computed(() => {
    const used = new Set(this.rows().map((row) => row.transportRouteId));
    return this.catalog().filter((route) => !used.has(route.id));
  });
  /**
   * The route that counts as default for this product: its own, else the vendor's (if on), else the first one on –
   * the same rule as the dispo (rows are sorted by identifier; a route inactive in the catalog is no candidate).
   */
  readonly defaultKey = computed(() => {
    const on = this.rows().filter((row) => !this.isNew(row) && !row.inactive && !this.catalogOf(row)?.inactive);
    return (on.find((row) => row.isDefault) ?? on.find((row) => row.vendor?.isDefault) ?? on[0])?.key ?? null;
  });

  private nextKey = 0;
  readonly trackByKey = (_index: number, row: ProductRouteRow): string => row.key;

  constructor() {
    effect(() => {
      const linkId = this.vendorPriceListAddressLinkId();
      this.addressId();
      if (linkId) {
        untracked(() => void this.load());
      }
    });
  }

  /**
   * Loads the tab. After an action (`keepEditsExcept` = the row acted on) typed but unsaved values of the other rows
   * and new rows not saved yet are kept – only the acted row shows what the server has now.
   */
  async load(keepEditsExcept?: string): Promise<void> {
    const kept = keepEditsExcept !== undefined ? this.rows().filter((r) => r.key !== keepEditsExcept && this.isDirty(r)) : [];
    this.loading.set(true);
    this.error.set(null);
    try {
      const [catalog, vendorRoutes, productRoutes] = await Promise.all([
        this.catalogService.getActiveRoutes(),
        this.addressId() ? this.addressRoutes.getRoutes({ addressId: this.addressId(), role: TransportRouteRole.CREDITOR }) : Promise.resolve([]),
        this.service.getRoutes(this.vendorPriceListAddressLinkId()),
      ]);
      this.catalog.set(catalog);
      const byRoute = new Map(productRoutes.map((p) => [p.transportRouteId, p]));
      const rows: ProductRouteRow[] = vendorRoutes.map((vendor) => this.toRow(vendor, byRoute.get(vendor.transportRouteId) ?? null));
      const vendorRouteIds = new Set(vendorRoutes.map((v) => v.transportRouteId));
      for (const own of productRoutes.filter((p) => !vendorRouteIds.has(p.transportRouteId))) {
        rows.push(this.toRow(null, own));
      }
      const edits = new Map(kept.filter((r) => !this.isNew(r)).map((r) => [r.transportRouteId, r]));
      const merged = rows.map((row) => {
        const edit = edits.get(row.transportRouteId);
        return edit ? { ...row, transitDays: edit.transitDays, fixedCost: edit.fixedCost, costPerUnit: edit.costPerUnit, notes: edit.notes } : row;
      });
      const sorted = merged.sort((a, b) => (this.catalogOf(a)?.identifier ?? '').localeCompare(this.catalogOf(b)?.identifier ?? ''));
      this.rows.set([...sorted, ...kept.filter((r) => this.isNew(r))]);
    } catch (error) {
      this.error.set(this.messageOf(error));
    } finally {
      this.loading.set(false);
    }
  }

  /** A route kept for this product only (the vendor does not have it). */
  addRow(): void {
    const first = this.unusedRoutes()[0];
    this.rows.update((rows) => [
      ...rows,
      { key: `new-${this.nextKey++}`, transportRouteId: first?.id ?? null, vendor: null, saved: null, transitDays: null, fixedCost: null, costPerUnit: null, notes: '', isDefault: false, inactive: false },
    ]);
  }

  routeOptions(row: ProductRouteRow): TransportRoute[] {
    const own = row.transportRouteId ? this.catalogById().get(row.transportRouteId) : undefined;
    return own ? [own, ...this.unusedRoutes()] : this.unusedRoutes();
  }

  catalogOf(row: ProductRouteRow): TransportRoute | undefined {
    return (row.transportRouteId && this.catalogById().get(row.transportRouteId)) || row.vendor?.transportRoute || row.saved?.transportRoute || undefined;
  }

  iconOf(row: ProductRouteRow): string {
    return transportRouteIcon(this.catalogOf(row)?.iconCss);
  }

  routeIcon(route: TransportRoute): string {
    return transportRouteIcon(route.iconCss);
  }

  /** What applies when the product field is empty: the vendor value, for transit days the catalog value. */
  fallbackTransit(row: ProductRouteRow): number | null {
    return row.vendor?.transitDays ?? this.catalogOf(row)?.transitDays ?? null;
  }

  /** Vendor amount as grey placeholder – PHX currency format, always two decimals. */
  moneyPlaceholder(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return '';
    }
    return this.uiLocale.formatCurrency(value, this.currencyCode());
  }

  update(row: ProductRouteRow, patch: Partial<ProductRouteRow>): void {
    this.rows.update((rows) => rows.map((r) => (r.key === row.key ? { ...r, ...patch } : r)));
  }

  isNew(row: ProductRouteRow): boolean {
    return !row.vendor && !row.saved;
  }

  /** Badge "product values" only while the product really overrides a value – not for a star or a switch-off alone. */
  overridesValues(row: ProductRouteRow): boolean {
    return !!row.saved && hasOwnValues(row.saved);
  }

  /** Tooltip of the star: why this route is (not) the default and what a click does. */
  defaultTip(row: ProductRouteRow): string {
    const t = (key: string) => this.i18n.translate(key);
    if (row.inactive) {
      return t(_('transport-route.product.inactive-tip'));
    }
    if (this.defaultKey() !== row.key) {
      return t(_('transport-route.product.set-default'));
    }
    if (row.isDefault) {
      return `${t(_('transport-route.product.default-product'))} · ${t(_('transport-route.product.clear-default'))}`;
    }
    return row.vendor?.isDefault ? t(_('transport-route.product.default-vendor')) : t(_('transport-route.product.default-first'));
  }

  /** Star: makes the route the product's default; on the product's own default it lifts it (the vendor's applies again). */
  async toggleDefault(row: ProductRouteRow): Promise<void> {
    if (row.inactive || this.isNew(row) || this.busyKey()) {
      return;
    }
    if (row.isDefault) {
      return this.saveFlags(row, { isDefault: false });
    }
    const previous = this.rows().find((r) => r.key !== row.key && r.isDefault && r.saved && r.vendor && !r.inactive && !hasOwnValues(r.saved));
    await this.run(row, async () => {
      // first the new default (the server clears the old one in the same transaction), then the old entry goes if
      // the star was all it carried – a failure in between leaves an empty entry, never a product without default
      await this.persist(row, savedValuesOf(row), { isDefault: true });
      if (previous) {
        await this.service.delete(previous.saved!.id);
      }
    });
  }

  /** Switches the route off for this product (it loses its star) or on again. */
  async toggleInactive(row: ProductRouteRow): Promise<void> {
    if (this.isNew(row) || this.busyKey()) {
      return;
    }
    await this.saveFlags(row, row.inactive ? { inactive: false } : { inactive: true, isDefault: false });
  }

  isDirty(row: ProductRouteRow): boolean {
    const saved = row.saved;
    if (!saved) {
      return this.isNew(row) || row.transitDays !== null || row.fixedCost !== null || row.costPerUnit !== null || !!row.notes;
    }
    return (
      row.transportRouteId !== saved.transportRouteId ||
      (row.transitDays ?? null) !== (saved.transitDays ?? null) ||
      (row.fixedCost ?? null) !== (saved.fixedCost ?? null) ||
      (row.costPerUnit ?? null) !== (saved.costPerUnit ?? null) ||
      (row.notes ?? '') !== (saved.notes ?? '')
    );
  }

  /** The check button: saves the typed values of the row. */
  async saveRow(row: ProductRouteRow): Promise<void> {
    if (!row.transportRouteId) {
      return;
    }
    await this.run(row, () => this.persist(row, row));
  }

  /** Star and switch-off: only the flag changes – values typed into the row but not saved are not saved with it. */
  private saveFlags(row: ProductRouteRow, flags: RouteFlags): Promise<void> {
    return this.run(row, () => this.persist(row, savedValuesOf(row), flags));
  }

  /**
   * The one place that writes a product entry: saved with the given values and flags – or removed when a vendor
   * route keeps nothing of its own (no value, no star, not switched off; the vendor values apply again).
   */
  private persist(row: ProductRouteRow, values: RouteValues, flags: RouteFlags = {}): Promise<unknown> {
    const isDefault = flags.isDefault ?? row.isDefault;
    const inactive = flags.inactive ?? row.inactive;
    if (row.vendor && !hasOwnValues(values) && !isDefault && !inactive) {
      return row.saved ? this.service.delete(row.saved.id) : Promise.resolve();
    }
    return this.service.save({
      id: row.saved?.id,
      vendorPriceListAddressLinkId: this.vendorPriceListAddressLinkId(),
      transportRouteId: row.transportRouteId,
      transitDays: values.transitDays,
      fixedCost: values.fixedCost,
      costPerUnit: values.costPerUnit,
      notes: values.notes || null,
      isDefault,
      inactive,
    });
  }

  /** Removes the product values – the vendor's route applies again; a product-only route disappears. Asks first. */
  async backToVendor(row: ProductRouteRow): Promise<void> {
    if (!row.saved) {
      this.revertRow(row);
      return;
    }
    const confirmed = await lastValueFrom(
      this.modal.showConfirmationDialog({
        title: row.vendor ? _('transport-route.product.back-to-vendor-title') : _('transport-route.product.remove-own-title'),
        message: row.vendor ? _('transport-route.product.back-to-vendor-message') : _('transport-route.product.remove-own-message'),
        variables: { route: this.catalogOf(row)?.identifier ?? '' },
        size: 'sm',
      }),
    ).catch(() => false);
    if (!confirmed) {
      return;
    }
    await this.run(row, () => this.service.delete(row.saved!.id));
  }

  revertRow(row: ProductRouteRow): void {
    if (row.vendor || row.saved) {
      this.rows.update((rows) => rows.map((r) => (r.key === row.key ? this.toRow(row.vendor, row.saved) : r)));
    } else {
      this.rows.update((rows) => rows.filter((r) => r.key !== row.key));
    }
  }

  private async run(row: ProductRouteRow, action: () => Promise<unknown>): Promise<void> {
    this.busyKey.set(row.key);
    this.error.set(null);
    try {
      await action();
      await this.load(row.key);
    } catch (error) {
      this.error.set(this.messageOf(error));
    } finally {
      this.busyKey.set(null);
    }
  }

  private toRow(vendor: AddressTransportRoute | null, saved: ProductVendorTransportRoute | null): ProductRouteRow {
    return {
      key: vendor?.id ?? saved?.id ?? `new-${this.nextKey++}`,
      transportRouteId: vendor?.transportRouteId ?? saved?.transportRouteId ?? null,
      vendor,
      saved,
      transitDays: saved?.transitDays ?? null,
      fixedCost: saved?.fixedCost ?? null,
      costPerUnit: saved?.costPerUnit ?? null,
      notes: saved?.notes ?? '',
      isDefault: !!saved?.isDefault,
      inactive: !!saved?.inactive,
    };
  }

  private messageOf(error: unknown): string {
    const message = (error as { message?: string })?.message ?? String(error);
    return this.i18n.translate(_('transport-route.address.save-error'), { message });
  }
}
